<template>
  <div class="screenshot-gallery">
    <!-- Thumbnail strip -->
    <div class="screenshot-gallery-thumbnails flex gap-2 flex-wrap">
      <div
        v-for="(url, idx) in urls"
        :key="idx"
        @click="openLightbox(idx)"
        class="relative cursor-pointer group"
      >
        <img
          :src="url"
          :alt="'Screenshot ' + (idx + 1)"
          class="w-20 h-14 object-cover rounded-md border border-gray-200 hover:border-indigo-400 transition-colors"
        />
        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-md transition-colors flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-white opacity-0 group-hover:opacity-100 drop-shadow transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Lightbox -->
    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="screenshot-gallery-lightbox fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
        @click.self="closeLightbox"
        @keydown.escape="closeLightbox"
        @keydown.left="prev"
        @keydown.right="next"
        tabindex="0"
        ref="lightboxRef"
      >
        <!-- Close button -->
        <button
          @click="closeLightbox"
          class="absolute top-4 right-4 text-white/70 hover:text-white z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Navigation arrows -->
        <button
          v-if="urls.length > 1"
          @click="prev"
          class="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          v-if="urls.length > 1"
          @click="next"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Image -->
        <img
          :src="urls[activeIndex]"
          :alt="'Screenshot ' + (activeIndex + 1)"
          class="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
        />

        <!-- Counter -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          {{ activeIndex + 1 }} / {{ urls.length }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'

const props = defineProps<{
  urls: string[]
}>()

const lightboxOpen = ref(false)
const activeIndex = ref(0)
const lightboxRef = ref(null)

function openLightbox(index) {
  activeIndex.value = index
  lightboxOpen.value = true
  nextTick(() => {
    if (lightboxRef.value) lightboxRef.value.focus()
  })
}

function closeLightbox() {
  lightboxOpen.value = false
}

function prev() {
  activeIndex.value = (activeIndex.value - 1 + props.urls.length) % props.urls.length
}

function next() {
  activeIndex.value = (activeIndex.value + 1) % props.urls.length
}
</script>
